import { Reason } from './Reason.js';
import { ActionButton, ActionInput } from '../parts/index.js';
// The rights holders on the admin Snowflake console (pipeline RC12, B2327): which company each OnePlatform rights holder
// is, proposed by name each night or by a person here, confirmed or rejected by a person, as the companies and the rights
// above it are (B178). A confirmed pairing is what lets a rights holder's record say who takes its content and what the
// booking system holds for it. The database decides who may confirm; this page only asks and shows the answer.
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ApiError, Unauthenticated, type Store } from '../api.js';
import { admin, type NearestOrganisation, type RightsHolderMapRow, type RightsHolderMapView, type UnreachedRightsHolder } from './api.js';
import { n } from './format.js';

function said(e: unknown): string {
  if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return 'signing you in again…'; }
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : String(e);
}

function stateCell(r: RightsHolderMapRow) {
  if (r.state === 'confirmed') return <>confirmed by {r.confirmedBy ?? 'a person'}{r.confirmedAt ? <><br /><span className="dim">{r.confirmedAt.slice(0, 10)}</span></> : null}</>;
  if (r.state === 'rejected') return <>rejected{r.rejectedAt ? <><br /><span className="dim">{r.rejectedAt.slice(0, 10)}</span></> : null}</>;
  return <span className="dim">proposed</span>;
}

const taken = (events: number, operators: number) => (events > 0 ? <>{n(events)}<div className="dim small">{n(operators)} operator{operators === 1 ? '' : 's'}</div></> : <span className="dim">none</span>);

function Unreached({ canSave, store, rows, busy, pick }: { canSave: boolean; store: Store; rows: UnreachedRightsHolder[]; busy: boolean; pick: (k: UnreachedRightsHolder, o: NearestOrganisation) => void }) {
  const [searching, setSearching] = useState<number | null>(null);
  const [term, setTerm] = useState('');
  const [found, setFound] = useState<NearestOrganisation[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  if (rows.length === 0) return null;
  const choose = (k: UnreachedRightsHolder, o: NearestOrganisation) => { setSearching(null); setFound(null); setTerm(''); setSearchError(null); pick(k, o); };
  const find = (e: FormEvent) => {
    e.preventDefault(); setSearchError(null); setFound(null);
    if (term.trim().length < 2) { setSearchError('Type at least two letters of the company\'s name.'); return; }
    admin.searchCompanies(store.tenantId, term.trim()).then((r) => setFound(r.organisations)).catch((x: unknown) => setSearchError(said(x)));
  };
  return (
    <details className="admin-unreached">
      <summary>
        <strong>{n(rows.length)} rights holders reach no company</strong>
        <span> · their names matched no company here, or a person rejected the match. Say which company each is.</span>
      </summary>
      <div className="admin-scroll">
        <table className="admin">
          <thead><tr><th>Rights holder in Snowflake</th><th className="num">Events taken, 12 months</th><th>Which company is it?</th></tr></thead>
          <tbody>
            {rows.map((k) => (
              <tr key={k.rightsholderId}>
                <td><strong>{k.name ?? 'no name in Snowflake'}</strong>
                  <div className="dim small">rights holder {k.rightsholderId}{k.rejectedOrganisation ? ` · a person said it is not ${k.rejectedOrganisation}` : ''}</div></td>
                <td className="num">{taken(k.eventsTaken12m, k.operators12m)}</td>
                <td>
                  <div className="admin-actions">
                    {k.nearest.map((o) => <ActionButton key={o.organisationId} className="small" type="button" disabled={busy || !canSave} onClick={() => choose(k, o)}>{o.name}</ActionButton>)}
                    {k.nearest.length === 0 ? <span className="dim small">no company of a similar name</span> : null}
                    <ActionButton reading className="small quiet" type="button" disabled={busy}
                      onClick={() => { setSearching(searching === k.rightsholderId ? null : k.rightsholderId); setTerm(''); setFound(null); setSearchError(null); }}>
                      {searching === k.rightsholderId ? 'Close' : 'Another company…'}
                    </ActionButton>
                  </div>
                  {searching === k.rightsholderId ? (
                    <form className="admin-find" onSubmit={find}>
                      <ActionInput reading aria-label={`company for ${k.name ?? k.rightsholderId}`} placeholder="company name" value={term} autoFocus onChange={(e) => setTerm(e.target.value)} />
                      <ActionButton reading className="small" type="submit">Find</ActionButton>
                      {searchError ? <span className="admin-says parked">{searchError}</span> : null}
                      {found !== null && found.length === 0 ? <span className="dim small">No company of that name in Cobrain.</span> : null}
                      {found && found.length > 0 ? (
                        <div className="admin-actions">
                          {found.map((o) => <ActionButton key={o.organisationId} className="small" type="button" disabled={busy || !canSave} onClick={() => choose(k, o)}>{o.name}</ActionButton>)}
                        </div>
                      ) : null}
                    </form>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

export function RightsHolders({ store }: { store: Store }) {
  const [data, setData] = useState<RightsHolderMapView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const canSave = reason.trim().length > 0;
  const [busy, setBusy] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [turn, setTurn] = useState(0);
  const reload = useCallback(() => setTurn((x) => x + 1), []);
  useEffect(() => {
    let cancelled = false;
    admin.rightsholders(store.tenantId).then((r) => { if (!cancelled) { setData(r.rightsholders); setError(null); } }).catch((e: unknown) => { if (!cancelled) setError(said(e)); });
    return () => { cancelled = true; };
  }, [store.tenantId, turn]);
  const act = (id: string, verb: 'confirm' | 'reject') => {
    if (!canSave || busy !== null) return;
    setBusy(id); setError(null); setPicked(null);
    admin.decide(store.tenantId, 'rightsholder', id, verb, reason.trim()).then(() => { setReason(''); reload(); }).catch((e: unknown) => setError(said(e))).finally(() => setBusy(null));
  };
  const pick = (k: UnreachedRightsHolder, o: NearestOrganisation) => {
    if (!canSave || busy !== null) return;
    setBusy(`rh:${k.rightsholderId}`); setError(null); setPicked(null);
    admin.proposeRightsHolder(store.tenantId, k.rightsholderId, o.organisationId, reason.trim())
      .then(() => { setReason(''); setPicked(`${k.name ?? k.rightsholderId} is now proposed for ${o.name}. It is in the list below: confirm it there, or reject it to choose again.`); reload(); })
      .catch((e: unknown) => setError(said(e))).finally(() => setBusy(null));
  };
  const c = data?.counts;
  return (
    <>
      <div className="admin-head"><h2>Rights holders</h2>
        <span>{c ? `${n(c.linked)} of ${n(c.inCopy)} rights holders linked · ${n(c.candidates)} proposed and waiting for a person · ${n(c.rejected)} rejected` : 'reading…'}</span></div>
      <Reason value={reason} onChange={setReason} busy={busy !== null} />
      <p className="admin-note">
        Which company each OnePlatform rights holder is. A linked rights holder&rsquo;s record says which operators take its
        content and what the booking system holds for it; one nobody has linked says neither.
      </p>
      {error ? <p className="admin-error" role="alert">{error} <ActionButton reading type="button" className="admin-retry" onClick={reload}>Try again</ActionButton></p> : null}
      {picked ? <p className="admin-says ok" role="status">{picked}</p> : null}
      {data ? (
        <>
          <Unreached canSave={canSave} store={store} rows={data.unreached} busy={busy !== null} pick={pick} />
          <div className="admin-scroll">
            <table className="admin">
              <thead><tr><th>Company</th><th>Rights holder in Snowflake</th><th>Matched by</th><th className="num">Events taken, 12 months</th><th>State</th><th /></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className={r.state === 'candidate' ? undefined : 'decided'}>
                    <td><strong>{r.organisationName}</strong></td>
                    <td>{r.rightsholderName ?? <span className="dim">no name in the copy</span>}<div className="dim small">rights holder {r.rightsholderId}</div></td>
                    <td>{r.basis ?? <span className="dim">{r.proposedName ? 'chosen by a person' : '—'}</span>}</td>
                    <td className="num">{taken(r.eventsTaken12m, r.operators12m)}</td>
                    <td>{stateCell(r)}</td>
                    <td>{r.state === 'candidate' ? (
                      <div className="admin-actions">
                        <ActionButton className="small" type="button" disabled={busy !== null || !canSave} onClick={() => act(r.id, 'confirm')}>Confirm</ActionButton>
                        <ActionButton className="small danger" type="button" disabled={busy !== null || !canSave} onClick={() => act(r.id, 'reject')}>Reject</ActionButton>
                      </div>
                    ) : null}</td>
                  </tr>
                ))}
                {data.rows.length === 0 ? <tr><td colSpan={6} className="dim">Nothing proposed yet.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </>
  );
}
